<template>
  <div class="cu-panel" :class="{ embedded }">
    <div class="cu-head" :class="{ 'cu-head--embedded': embedded }">
      <div class="cu-title-wrap">
        <span class="cu-title">{{ catchupTitle }}</span>
        <span class="cu-leader-fb" :title="leaderFbTitle">{{ leaderFbText }}</span>
      </div>
      <div class="cu-tabs">
        <button
          type="button"
          class="cu-tab"
          :class="{ active: tab === 'list' }"
          @click="$emit('change-tab', 'list')"
        >补涨列表</button>
        <button
          type="button"
          class="cu-tab"
          :class="{ active: tab === 'metrics' }"
          @click="$emit('change-tab', 'metrics')"
        >补涨指标</button>
      </div>
    </div>

    <p v-if="emptyHint" class="cu-empty">{{ emptyHint }}</p>
    <div v-else-if="tab === 'list'" class="cu-list">
      <div class="cu-cols">
        <span class="cu-idx">#</span>
        <span>股票名称</span>
        <span class="cu-board-h">板位</span>
        <span class="cu-score-h">总分</span>
        <span class="cu-num-h">中期筹码</span>
        <span class="cu-num-h">流通市值</span>
        <span>概念详情</span>
      </div>
      <div
        v-for="(row, idx) in visibleRows"
        :key="row.code"
        class="cu-row"
        @click="$emit('open-row', row)"
      >
        <span class="cu-idx">{{ prefix }}{{ idx + 1 }}</span>
        <div class="cu-namebox">
          <div class="cu-name">{{ row.name }}</div>
          <div class="cu-code">{{ row.code }}</div>
        </div>
        <span class="cu-board" :title="boardTitle(row)">
          <span
            v-if="row.nodeHit"
            class="cu-node-star"
            :style="nodeStarStyle(row.nodeScore)"
          >★</span>
          <span class="cu-board-label" :style="row.nodeHit ? nodeStarStyle(row.nodeScore) : null">{{ boardText(row) }}</span>
        </span>
        <span class="cu-score" :title="'补涨分析同分 · 阈值≥6'">{{ scoreText(row) }}</span>
        <span class="cu-num">{{ midChipText(row) }}</span>
        <span class="cu-num">{{ freeFloatText(row) }}</span>
        <span class="cu-attr" :title="row.conceptDetail || '未命中点选主属性'">{{ row.conceptDetail || '—' }}</span>
      </div>
    </div>

    <div v-else-if="tab === 'metrics'" class="cu-metrics-wrap">
      <p class="cu-empty">补涨指标待接入 · 接口 quality=NOT_IMPLEMENTED</p>
    </div>

    <button
      v-if="rows.length > previewLimit && !expanded"
      type="button"
      class="cu-more"
      @click="$emit('expand')"
    >查看全部补涨（{{ rows.length }}） →</button>
    <button
      v-else-if="expanded && rows.length > previewLimit"
      type="button"
      class="cu-more"
      @click="$emit('collapse')"
    >收起</button>
    <p class="cu-foot">{{ footText }}</p>
  </div>
</template>

<script>
/** 与竞价 TOP 同源：节点分 ≥7 起用青系强色 */
function nodeScoreColor(score) {
  const s = Number(score)
  if (!Number.isFinite(s) || s < 3) return null
  const clamp = (x, a, b) => Math.max(a, Math.min(b, x))
  const lerp = (a, b, t) => a + (b - a) * t
  if (s < 7) {
    const t = (s - 3) / 4
    const sat = Math.round(lerp(22, 46, t))
    const light = Math.round(lerp(78, 58, t))
    return `hsl(180, ${sat}%, ${light}%)`
  }
  const t = clamp((s - 7) / 15, 0, 1)
  const sat = Math.round(lerp(82, 88, t))
  const light = Math.round(lerp(42, 24, t))
  return `hsl(180, ${sat}%, ${light}%)`
}

export default {
  name: 'CatchupPanel',
  props: {
    rows: { type: Array, default: () => [] },
    tab: { type: String, default: 'list' },
    expanded: { type: Boolean, default: false },
    prefix: { type: String, default: '' },
    previewLimit: { type: Number, default: 5 },
    /** none | loading | ready | no_attr */
    catchupStatus: { type: String, default: 'none' },
    selectedAttr: { type: String, default: '' },
    /** 嵌在卡片背面时压缩外边距 */
    embedded: { type: Boolean, default: false },
    /**
     * 龙头首板匹配（与首板预选同源）：
     * { closePrice, freeFloatMarketCapYi, firstLimitUpDate, loading }
     */
    leaderFirstBoard: { type: Object, default: null }
  },
  computed: {
    hasRows() {
      return !!(this.rows && this.rows.length)
    },
    catchupTitle() {
      if (this.catchupStatus === 'no_attr' || !this.selectedAttr) return '补涨观察'
      if (this.catchupStatus === 'loading') return '补涨观察（匹配中）'
      if (this.catchupStatus === 'ready') return '补涨观察（' + this.rows.length + '）'
      return '补涨观察'
    },
    leaderFb() {
      return this.leaderFirstBoard || {}
    },
    leaderFbText() {
      if (this.leaderFb.loading) return '首板价 … · 首板流通 …'
      const price = this.fmtPrice(this.leaderFb.closePrice)
      const ff = this.fmtYi(this.leaderFb.freeFloatMarketCapYi)
      return '首板价 ' + price + ' · 首板流通 ' + ff
    },
    leaderFbTitle() {
      const d = this.leaderFb.firstLimitUpDate
      if (d) return '参考日近15交易日内首次涨停日 ' + d + ' 的收盘价与自由流通市值（与首板匹配同源）'
      return '参考日近15交易日内首次涨停日的收盘价与自由流通市值（与首板匹配同源）'
    },
    emptyHint() {
      if (this.tab !== 'list') return ''
      if (this.hasRows) return ''
      if (this.catchupStatus === 'loading') return '正在匹配所选交易日涨停池…'
      if (!this.selectedAttr || this.catchupStatus === 'no_attr') {
        return '请先选择人工主属性（概念）后自动匹配'
      }
      if (this.catchupStatus === 'ready') {
        return '所选日期涨停池中无总分≥6 的匹配（空≠已确认无补涨）'
      }
      return '请先选择人工主属性（概念）后自动匹配'
    },
    footText() {
      if (!this.selectedAttr) return '选择主属性后按补涨分析口径匹配涨停池（总分≥6）。'
      return '主属性「' + this.selectedAttr + '」· 概念详情仅显示点选主属性命中；未命中为空。节点★=首板日节点分≥7，颜色同竞价TOP。'
    },
    visibleRows() {
      if (this.expanded) return this.rows
      return (this.rows || []).slice(0, this.previewLimit)
    }
  },
  methods: {
    scoreText(row) {
      if (row && row.totalScore != null && Number.isFinite(Number(row.totalScore))) {
        return String(Number(row.totalScore))
      }
      const s = row && row.matchScore
      if (s && s.value != null) return String(s.value)
      return '—'
    },
    boardText(row) {
      if (row && row.consecutiveBoardsLabel) return row.consecutiveBoardsLabel
      const n = row && Number(row.consecutiveBoards)
      if (Number.isFinite(n) && n > 0) return n + 'B'
      return '—'
    },
    boardTitle(row) {
      const parts = [this.boardText(row)]
      const ns = Number(row && row.nodeScore)
      if (Number.isFinite(ns)) {
        parts.push('节点分 ' + ns)
        if (row.nodeHit) parts.push('达标≥7')
      }
      if (row && row.firstBoardDate) parts.push('首板日 ' + row.firstBoardDate)
      return parts.join(' · ')
    },
    nodeStarStyle(score) {
      const c = nodeScoreColor(score)
      return c ? { color: c } : null
    },
    midChipText(row) {
      const n = row && Number(row.midChip)
      if (!Number.isFinite(n)) return '—'
      return (n * 100).toFixed(2) + '%'
    },
    freeFloatText(row) {
      const n = row && Number(row.freeFloatMarketCap)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(1) + '亿'
    },
    fmtPrice(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(2)
    },
    fmtYi(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(2) + '亿'
    }
  }
}
</script>

<style lang="scss" scoped>
.cu-panel {
  background: #f4f8ff;
  border-radius: 10px;
  padding: 8px 8px 6px;
  margin-top: 8px;
  &.embedded {
    background: transparent;
    border-radius: 0;
    padding: 0;
    margin-top: 0;
  }
}
.cu-head--embedded {
  margin-bottom: 6px;
}
.cu-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.cu-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.cu-title {
  font-size: 12px;
  font-weight: 700;
  color: #1d4f91;
}
.cu-leader-fb {
  font-size: 10px;
  color: #5b6b82;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cu-tabs {
  display: flex;
  flex-shrink: 0;
  background: #e7eefc;
  border-radius: 8px;
  padding: 2px;
}
.cu-tab {
  border: 0;
  background: transparent;
  color: #4a6fa3;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 6px;
  cursor: pointer;
  &.active {
    background: #fff;
    color: #1a73e8;
    font-weight: 700;
  }
}
.cu-list {
  overflow-x: auto;
}
.cu-cols,
.cu-row {
  display: grid;
  grid-template-columns:
    24px
    minmax(64px, 1.1fr)
    minmax(52px, 0.7fr)
    34px
    minmax(52px, 0.7fr)
    minmax(48px, 0.65fr)
    minmax(56px, 0.9fr);
  gap: 3px;
  align-items: center;
  padding: 5px 2px;
  font-size: 10px;
  min-width: 340px;
}
.cu-cols {
  color: #6b82a6;
  font-weight: 650;
  border-bottom: 1px solid #d7e4f6;
  padding-bottom: 4px;
}
.cu-score-h,
.cu-num-h,
.cu-board-h { text-align: right; }
.cu-row {
  border-bottom: 1px solid #e4edf8;
  cursor: pointer;
  &:hover { background: #fff; }
}
.cu-idx { color: #8aa0c2; font-weight: 700; }
.cu-name { color: #1f2d3d; font-weight: 650; line-height: 1.2; }
.cu-code { color: #8aa0c2; font-size: 9px; }
.cu-attr { color: #5b6b82; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cu-score { color: #1a73e8; font-weight: 700; text-align: right; }
.cu-num { color: #3a4a63; text-align: right; font-variant-numeric: tabular-nums; }
.cu-board {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  text-align: right;
  color: #3a4a63;
  font-weight: 650;
  white-space: nowrap;
}
.cu-node-star {
  color: #13c2c2;
  font-size: 11px;
  line-height: 1;
  font-weight: 700;
}
.cu-metrics-wrap { overflow-x: auto; }
.cu-more {
  width: 100%;
  margin-top: 6px;
  border: 0;
  background: transparent;
  color: #1a73e8;
  cursor: pointer;
  font-size: 11px;
  padding: 4px 0;
}
.cu-empty {
  margin: 4px 0;
  font-size: 11px;
  color: #ad6800;
}
.cu-foot {
  margin: 4px 0 0;
  font-size: 10px;
  color: #90a4c0;
  line-height: 1.35;
}
</style>
